import {
  Box,
  CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BaseHighchart from '../../BaseHighchart';
import {
  ChartDataType,
} from '../../types';

import { WIND_NAME, CHART_TITLE_VIENTO } from '../../../../constants/graphs';
import apiClient from '../../../../requests/api/apiClient';
import { ConversationSchema, TimePointSchema } from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';
import { navigateToChart } from '../../../../utils/helpers';
import { FetchError } from '../../../../views/types';
import ChartFilter from '../../ChartFilter';
import ConversationModal from '../../ConversationTab/conversationModal';
import ChartConfigDialog from '../../ChartConfigDialog/ChartConfigDialog';
import WindConfigContent from './WindConfigContent';
import {
  calculateYAxisLimits,
  getChartConfig,
  getStackedTooltip,
  getYAxis,
} from '../utils';
import useChartConfigClick from '../../../../hooks/useChartConfigClick';

function WindChart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideXValues = false,
  hideYValues = false,
  height = 600,
  title = CHART_TITLE_VIENTO,
  yAxisTitle = 'Velocidad y ráfaga de viento (Km/h)',
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  onConversationCreated,
  isWeatherStation,
}: {
  chartView: 'dashboard' | 'graphs' | 'settings' | 'fullScreen'
  sectorId: string
  farmId: string
  hideLegend?: boolean
  hideXValues?: boolean
  hideYValues?: boolean
  height?: number | string
  title?: string
  yAxisTitle?: string
  externalModalState?: {
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  };
  externalSetModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>>;
  externalConversations?: ConversationSchema[];
  externalSetConversations?: React.Dispatch<React.SetStateAction<ConversationSchema[]>>;
  onConversationCreated?: () => void;
  isGeneratingPDF?: boolean;
  isWeatherStation?: boolean;
}) {
  const chartName = 'viento';
  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    categories: [],
    absoluteCategories: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [internalModalState, setInternalModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);

  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (interval) {
        const { data } = await apiClient.readings.getWindVelocityData({
          farmId,
          sectorId,
          startDate: interval?.startDate || '',
          endDate: interval?.endDate || '',
          telemetryType: selectedSector?.telemetryType || 'wiseconn',
          sn: selectedSector?.telemetryType === 'sn' ? selectedSector.weatherZoneId : undefined,
          chart: WIND_NAME,
          isWeatherStation,
        });

        setChartData({
          series: [...data.velocitySeries, ...data.gustSeries],
          absoluteCategories: data.absoluteCategories,
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [farmId,
    interval,
    selectedSector,
    sectorId,
    isWeatherStation,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const customTooltip = useMemo(() => ({
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      return getStackedTooltip(this, conversations);
    },
  }), [conversations]);

  const chartConfig = useMemo(() => {
    if (!interval) return null;

    const yLimits = calculateYAxisLimits(chartData.series as any[]);

    return getChartConfig(
      chartData,
      height,
      hideXValues,
      {
        ...(getYAxis(yAxisTitle, hideYValues) as Highcharts.YAxisOptions),
        min: yLimits.min,
        max: yLimits.max,
      },
      dayjs(interval.startDate).valueOf(),
      dayjs(interval.endDate).valueOf(),
    );
  }, [
    hideXValues,
    chartData.absoluteCategories,
    hideYValues,
    yAxisTitle,
    height,
    interval,
  ]);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(
            sectorId,
            WIND_NAME,
          );
          setInternalConversations(data.conversations);
        } catch (err) {
          const { error } = err as FetchError;
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <BaseHighchart
        chartView={chartView}
        sectorId={sectorId}
        filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
        title={title}
        yAxisTitle={yAxisTitle}
        series={chartData.series as Highcharts.SeriesOptionsType[]}
        tooltip={customTooltip}
        hideYValues={hideYValues}
        showFullScreen={true}
        legend={{ enabled: !hideLegend }}
        chartConfig={chartConfig}
        onConfigClick={handleConfigClick}
        onFullScreenClick={handleFullScreenClick}
        absoluteCategories={chartData.absoluteCategories}
        setModalState={setModalState}
        conversations={conversations}
        height={height}
        zoomSync={true}
        autoAdjustYAxis={true}
        interval={interval}
        graphName={WIND_NAME}
      />
      <ConversationModal
        modalState={modalState}
        setModalState={setModalState}
        farmId={farmId}
        sectorId={sectorId}
        chart={WIND_NAME}
        setConversationsFunction={setConversations}
        onConversationCreated={onConversationCreated}
      />
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · Viento"
      >
        <WindConfigContent sectorId={sectorId} />
      </ChartConfigDialog>
    </div>
  );
}

export default WindChart;
